import React, { useState } from "react";
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Paper,
  MantineProvider,
  Group,
  Card,
  Transition,
  List,
  Image,
  Box,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconArrowRight,
  IconFileDescription,
  IconMailAi,
  IconShield,
  IconCheck,
  IconGavel,
  IconPaperclip,
} from "@tabler/icons-react";
import {Footer} from "../Footer/Footer";
import styles from "../SplashLanding.module.css";
import ParticleAnimation from "../ParticleAnimation";
import { ContactModal } from "../ContactModal";
import {Header} from "../Header/Header";

const theme = {
  components: {
    Button: {
      styles: {
        root: {
          transition: "background-color 0.3s ease-in-out",
          "&:hover": {
            backgroundColor: "rgba(15, 45, 45, 0.75)",
            color: "white",
          },
        },
      },
    },
  },
};

const EmergingCompaniesVentureCapital = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 48em)");

  const buttonStyles = {
    backgroundColor: "var(--accent-gold)",
    transition: "background-color 0.3s ease-in-out",
    fontSize: isMobile ? "1rem" : "1.25rem",
    width: "100%",
    maxWidth: "480px",
  };

  const handleMouseEnter = (e) => {
    e.currentTarget.style.backgroundColor = "var(--accent-gold-hover)";
  };

  const handleMouseLeave = (e) => {
    e.currentTarget.style.backgroundColor = "var(--accent-gold)";
  };

  const renderButton = (text, onClick) => (
    <Button
      size={isMobile ? "md" : "xl"}
      className={styles.getStartedBtn}
      style={buttonStyles}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      rightSection={<IconArrowRight size={18} />}
      onClick={onClick}
    >
      {text}
    </Button>
  );

  const renderGetStarted = () =>
    renderButton("Get Started", () => window.location.href = "/pricing");

  const renderConsultation = () =>
    renderButton("Request a Free Consultation", openContactModal);

  const FeatureCard = ({ icon: Icon, title, description, style }) => (
    <Card
      withBorder
      shadow="lg"
      padding="lg"
      className={styles.feature}
      style={{ ...style, padding: "20px" }}
    >
      <Card.Section p="lg">
        <Icon
          size={64}
          stroke={1.5}
          color="rgba(15,45,45,.9)"
          className={styles.featureIcon}
        />
      </Card.Section>
      <Title order={4} mt="sm">
        {title}
      </Title>
      <Text mt="xs">{description}</Text>
    </Card>
  );

  const renderFeatures = () => {
    const features = [
      {
        icon: IconFileDescription,
        title: "Draft Legal Documents Instantly Delivered to your Inbox",
        description:
          "We use smart precedents to ensure consistent, quality drafting",
      },
      {
        icon: IconPaperclip,
        title: "Forward Documents to Socius for Processing Tasks",
        description: "Convert DOCX to PDF, run comparisons, and more",
      },
      {
        icon: IconMailAi,
        title: "Chat, Q&A, Summarization, Research and Draft Correspondence",
        description:
          "Leverage the world's most powerful LLMs through our secure email interface",
      },
    ];

    return (
      <Group
        justify="center"
        gap="xl"
        my="xl"
        style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}
      >
        {features.map((feature, index) => (
          <Transition
            key={index}
            mounted={true}
            transition="slide-up"
            duration={400}
            timingFunction="ease"
          >
            {(transitionStyles) => (
              <FeatureCard
                {...feature}
                style={{
                  flex: "1 1 calc(33% - 20px)",
                  minWidth: "320px",
                  maxWidth: "360px",
                }}
              />
            )}
          </Transition>
        ))}
      </Group>
    );
  };

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  return (
    <MantineProvider theme={theme}>
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <div
          style={{
            backgroundImage: "url('/static/socius hero.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            height: "100vh",
            width: "100%",
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: -1,
          }}
        />

        <ParticleAnimation />
        {Header()}

        <Container size="lg" py="xl">
          <Stack gap="xs" align="center">
            <Title
              order={1}
              ta="center"
              mb="sm"
              mt="xl"
              maw={900}
              className={styles.heroTitle}
              size={isMobile ? "h2" : "h1"}
            >
              <strong style={{ opacity: 1 }}>
                Accelerate Your VC Practice
              </strong>{" "}
              with AI-Powered Precision
            </Title>
            <Title
              order={3}
              fw={400}
              ta="center"
              mb="xs"
              maw={640}
              size={isMobile ? "h4" : "h3"}
            >
              Our AI-powered template assistant helps you draft complete sets of
              NVCA model forms and other agreements in <strong>minutes</strong>,
              not <strong>hours</strong>.
            </Title>
            <Box
              mt="xl"
              style={{
                width: "100%",
                maxWidth: "500px",
                textAlign: "center",
                zIndex: 10,
              }}
            >
              {renderGetStarted()}
            </Box>
            <Box
              mt="xl"
              style={{
                width: "100%",
                maxWidth: "100%",
                textAlign: "center",
                zIndex: 10,
              }}
            >
              {renderFeatures()}
            </Box>
            <Paper withBorder p="xl" radius="md" mb="xl" maw={800}>
              <List spacing="xl" size={isMobile ? "md" : "xl"} mb="sm" center>
                <List.Item icon={<IconShield size={32} />}>
                  <Title order={3}>Security</Title>Your data is protected with
                  enterprise-grade security. For more information, visit our{" "}
                  <a href="https://sociuslegal.com/static/policies.html#h.2smen67czmw">
                    Security & Privacy Center
                  </a>
                  .
                </List.Item>
                <List.Item icon={<IconCheck size={32} />}>
                  <Title order={3}>Simplicity</Title>No complex installations
                  required, our AI-powered email assistant integrates seamlessly
                  into your existing workflow.
                </List.Item>
                <List.Item icon={<IconGavel size={32} />}>
                  <Title order={3}>Serious Legal Expertise</Title>Developed by a
                  lawyer, Socius is carefully designed to deliver the precision,
                  confidentiality, and professionalism essential for legal
                  practice.
                </List.Item>
              </List>
            </Paper>

            <Paper
              withBorder
              p="xl"
              my="xl"
              shadow="xl"
              radius="md"
              ta="center"
              style={{ maxWidth: "720px" }}
            >
              <Title order={1} mb="md" size={isMobile ? "h2" : "h1"}>
                AI Integration Services
              </Title>
              <Text size={isMobile ? "md" : "lg"} mb="xs">
                Let us help your firm optimize its VC practice with cutting-edge
                AI technology.
              </Text>

              <Box mt="md" mb="md" style={{ textAlign: "center" }}>
                {renderConsultation()}
              </Box>

              <hr
                style={{
                  margin: "20px 0",
                  border: "1px solid",
                  borderColor: "currentColor",
                }}
              />

              <Text size={isMobile ? "md" : "lg"} mb="xs">
                To learn more about AI and attorney professional responsibility,
                read our{" "}
                <a href="https://sociuslegal.com/static/policies.html#h.2smen67czmw">
                  AI Ethics & Professional Responsibility Guide
                </a>
                .
              </Text>
            </Paper>
            <Image
              src="/static/Socius_Full_DRK.svg"
              alt="Socius Legal"
              w={180}
              className={styles.logo}
            />
          </Stack>
        </Container>

        {Footer()}
        <ContactModal isOpen={isContactModalOpen} onClose={closeContactModal} />
      </div>
    </MantineProvider>
  );
};

export default EmergingCompaniesVentureCapital;
